import Phaser from 'phaser';

const SKY_TOP = 0x0b0620;
const SKY_MID = 0x4c1d95;
const SKY_BOT = 0xc2410c;

const PLANET_DARK = 0x6d28d9;
const PLANET_MID = 0xa855f7;
const PLANET_BAND = 0xf59e0b;
const PLANET_RING = 0xfde68a;

const ASTEROID = 0x44403c;
const ASTEROID_LIGHT = 0x78716c;

const NEBULA_A = 0x7c3aed;
const NEBULA_B = 0xec4899;

const STAR_BRIGHT = 0xffffff;
const SHOOTING_TRAIL = 0xfde68a;

export function drawCosmicSkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.55) {
      const u = t / 0.55;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.55) / 0.45;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  drawNebulaCloud(g, w * 0.18, h * 0.28, w * 0.3, NEBULA_A, 0.25);
  drawNebulaCloud(g, w * 0.75, h * 0.18, w * 0.22, NEBULA_B, 0.2);

  const rand = mulberry32(2024);
  for (let i = 0; i < Math.max(60, Math.floor(w * h / 12000)); i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.75;
    const sr = rand() < 0.15 ? 1.8 : 1;
    g.fillStyle(STAR_BRIGHT, rand() < 0.3 ? 1 : 0.7);
    g.fillCircle(sx, sy, sr);
  }

  for (let i = 0; i < 3; i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.3;
    const trailLen = 16 + rand() * 14;
    g.lineStyle(2, SHOOTING_TRAIL, 0.55);
    g.beginPath();
    g.moveTo(sx, sy);
    g.lineTo(sx + trailLen, sy + trailLen * 0.3);
    g.strokePath();
    g.fillStyle(STAR_BRIGHT, 1);
    g.fillCircle(sx, sy, 1.5);
  }
}

export function drawCosmicSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  const baselineY = Math.floor(h * 0.82);
  const cx = w * 0.72;
  const cy = baselineY + 20;
  const planetR = Math.min(h * 0.36, 260);

  g.fillStyle(PLANET_DARK, 1);
  g.fillCircle(cx, cy, planetR);

  g.fillStyle(PLANET_MID, 1);
  g.beginPath();
  g.arc(cx, cy, planetR, -Math.PI, 0, false);
  g.closePath();
  g.fillPath();

  g.fillStyle(PLANET_BAND, 0.65);
  for (let b = 0; b < 3; b++) {
    const bandY = cy - planetR * 0.55 + b * planetR * 0.32;
    g.fillRect(cx - planetR * 0.95, bandY, planetR * 1.9, planetR * 0.06);
  }

  g.lineStyle(3, PLANET_RING, 0.7);
  g.beginPath();
  g.arc(cx, cy, planetR * 1.18, Math.PI * 0.15, Math.PI * 0.85, false);
  g.strokePath();
  g.lineStyle(2, PLANET_RING, 0.4);
  g.beginPath();
  g.arc(cx, cy, planetR * 1.28, Math.PI * 0.1, Math.PI * 0.9, false);
  g.strokePath();

  const rand = mulberry32(707);
  for (let i = 0; i < 5; i++) {
    const ax = rand() * w;
    const ay = baselineY - 20 - rand() * 60;
    const ar = 3 + rand() * 5;
    g.fillStyle(ASTEROID, 1);
    g.fillCircle(ax, ay, ar);
    g.fillStyle(ASTEROID_LIGHT, 0.6);
    g.fillCircle(ax - ar * 0.3, ay - ar * 0.3, ar * 0.4);
  }

  g.fillStyle(0x030712, 0.95);
  g.fillRect(0, baselineY + 5, w, h - baselineY);
}

function drawNebulaCloud(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  size: number,
  color: number,
  alpha: number,
) {
  const rand = mulberry32(Math.floor(cx + cy));
  for (let i = 0; i < 14; i++) {
    const dx = (rand() - 0.5) * size;
    const dy = (rand() - 0.5) * size * 0.55;
    const r = 12 + rand() * size * 0.25;
    g.fillStyle(color, alpha);
    g.fillCircle(cx + dx, cy + dy, r);
  }
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
